// Drifting dots and outlined squares that lean toward the pointer. Ported from the merchant's original trust section.
// Colors come from data-colors (comma list). Runs only while on screen; draws a still frame under reduced motion.
class SParticleField extends HTMLElement {
  #canvas = null;
  #ctx = null;
  #particles = [];
  #pointer = { x: 0, y: 0, active: false };
  #frame = 0;
  #running = false;
  #width = 0;
  #height = 0;
  #controller = null;
  #observer = null;
  #resizer = null;

  connectedCallback() {
    this.#canvas = document.createElement('canvas');
    this.#canvas.setAttribute('aria-hidden', 'true');
    this.#canvas.className = 'block size-full';
    this.replaceChildren(this.#canvas);
    this.#ctx = this.#canvas.getContext('2d');
    if (!this.#ctx) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia('(pointer: fine)').matches;

    this.#resizer = new ResizeObserver(() => this.#resize());
    this.#resizer.observe(this);
    this.#resize();
    this.#seed();

    if (reduce) {
      this.#draw();
      return;
    }

    const target = this.parentElement ?? this;
    if (fine) {
      target.addEventListener('pointermove', (event) => {
        const rect = this.getBoundingClientRect();
        this.#pointer.x = event.clientX - rect.left;
        this.#pointer.y = event.clientY - rect.top;
        this.#pointer.active = true;
      }, { passive: true, signal });
      target.addEventListener('pointerleave', () => { this.#pointer.active = false; }, { signal });
    } else {
      // Touch screens: particles react to the finger while it moves across the section.
      const onTouch = (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        const rect = this.getBoundingClientRect();
        this.#pointer.x = touch.clientX - rect.left;
        this.#pointer.y = touch.clientY - rect.top;
        this.#pointer.active = true;
      };
      target.addEventListener('touchstart', onTouch, { passive: true, signal });
      target.addEventListener('touchmove', onTouch, { passive: true, signal });
      target.addEventListener('touchend', () => { this.#pointer.active = false; }, { passive: true, signal });
      target.addEventListener('touchcancel', () => { this.#pointer.active = false; }, { passive: true, signal });
    }

    this.#observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.#start();
      else this.#stop();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#resizer?.disconnect();
  }

  #resize() {
    const rect = this.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.#width = rect.width;
    this.#height = rect.height;
    this.#canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.#canvas.height = Math.max(1, Math.round(rect.height * dpr));
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!this.#running) this.#draw();
  }

  #seed() {
    const colors = (this.dataset.colors || '#F76302,#14120F,#CBD5E1').split(',').map((c) => c.trim());
    const [accent, ink, soft] = [colors[0], colors[1] ?? colors[0], colors[2] ?? colors[0]];
    const count = Number(this.dataset.count) || 20;
    this.#particles = Array.from({ length: count }, () => {
      const isDot = Math.random() > 0.2;
      const baseVx = (Math.random() - 0.5) * 0.18;
      const baseVy = (Math.random() - 0.5) * 0.18;
      return {
        type: isDot ? 'dot' : 'square',
        x: Math.random() * this.#width,
        y: Math.random() * this.#height,
        baseVx, baseVy, vx: baseVx, vy: baseVy,
        size: isDot ? (Math.random() > 0.6 ? Math.random() * 2 + 1 : Math.random() * 1.8 + 2.5) : Math.random() * 12 + 20,
        color: isDot ? (Math.random() > 0.45 ? accent : ink) : (Math.random() > 0.5 ? soft : accent),
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.0028,
      };
    });
  }

  #start() {
    if (this.#running) return;
    this.#running = true;
    const tick = () => {
      this.#update();
      this.#draw();
      this.#frame = requestAnimationFrame(tick);
    };
    this.#frame = requestAnimationFrame(tick);
  }

  #stop() {
    this.#running = false;
    cancelAnimationFrame(this.#frame);
  }

  #update() {
    const threshold = 165;
    for (const p of this.#particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vx += (p.baseVx - p.vx) * 0.04;
      p.vy += (p.baseVy - p.vy) * 0.04;
      if (this.#pointer.active) {
        const dx = this.#pointer.x - p.x;
        const dy = this.#pointer.y - p.y;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < threshold) {
          const force = ((threshold - dist) / threshold) * (p.type === 'dot' ? 0.07 : 0.032);
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }
      }
      const pad = p.size / 2;
      if (p.x < pad || p.x > this.#width - pad) {
        p.x = Math.min(Math.max(p.x, pad), this.#width - pad);
        p.vx *= -1; p.baseVx *= -1;
      }
      if (p.y < pad || p.y > this.#height - pad) {
        p.y = Math.min(Math.max(p.y, pad), this.#height - pad);
        p.vy *= -1; p.baseVy *= -1;
      }
      if (p.type === 'square') p.angle += p.spin;
    }
  }

  #draw() {
    const ctx = this.#ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, this.#width, this.#height);
    for (const p of this.#particles) {
      ctx.save();
      if (p.type === 'dot') {
        ctx.globalAlpha = 0.75;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
      } else {
        ctx.globalAlpha = 0.42;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(-p.size / 2, -p.size / 2, p.size, p.size, 7);
        else ctx.rect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 1.1;
        ctx.stroke();
      }
      ctx.restore();
    }
  }
}

if (!customElements.get('s-particle-field')) customElements.define('s-particle-field', SParticleField);
